import { create } from "zustand";
import type { TelegramUser } from "@/lib/telegram";
import {
  authenticateWithTelegram,
  setAccessToken,
} from "@/lib/api";

interface AuthState {
  user: TelegramUser | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  error: string | null;
  setUser: (user: TelegramUser) => void;
  setToken: (token: string) => void;
  initializeSession: (
    user: TelegramUser | null,
    initData: string,
  ) => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isInitializing: true,
  error: null,

  setUser: (user) => set({ user }),

  setToken: (token) => {
    setAccessToken(token);
    set({ token, isAuthenticated: true });
  },

  initializeSession: async (user, initData) => {
    set({ user, isInitializing: true, error: null, isAuthenticated: false });

    if (!user || !initData) {
      set({ isInitializing: false });
      return;
    }

    try {
      const tokens = await authenticateWithTelegram(initData);
      set({
        token: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        isAuthenticated: true,
        isInitializing: false,
      });
    } catch (error) {
      set({
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isInitializing: false,
        error: error instanceof Error ? error.message : "Authentication failed",
      });
    }
  },

  logout: () => {
    setAccessToken(null);
    set({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      error: null,
    });
  },
}));
